import { Box, Icon, Line, Text, as, color, toRem } from 'folds';
import {
  EventTimelineSet,
  IEventRelation,
  MsgType,
  Room,
  THREAD_RELATION_TYPE,
} from 'matrix-js-sdk';
import React, { MouseEventHandler, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { getMemberDisplayName, trimReplyFromBody } from '../../utils/room';
import { getMxIdLocalPart } from '../../utils/matrix';
import { LinePlaceholder } from './placeholder';
import { randomNumberBetween } from '../../utils/common';
import * as css from './Reply.css';
import { MessageBadEncryptedContent, MessageDeletedContent, MessageFailedContent } from './content';
import { scaleSystemEmoji } from '../../plugins/react-custom-html-parser';
import { useRoomEvent } from '../../hooks/useRoomEvent';
import { MessageSquareTextIcon } from '../../icons/MessageSquareTextIcon';

export const FORKS_FROM_RELATION = 'vip.elevo.forks_from';
type ElevoEventRelation = IEventRelation & {
  [FORKS_FROM_RELATION]?: {
    event_id?: unknown;
  };
};

type ReplyLayoutProps = {
  username?: string;
};
export const ReplyLayout = as<'div', ReplyLayoutProps>(
  ({ username, className, children, ...props }, ref) => {
    const { t } = useTranslation();
    return (
      <Box
        className={classNames(css.Reply, className)}
        alignItems="Center"
        gap="100"
        {...props}
        ref={ref}
      >
        <Box style={{ maxWidth: toRem(200) }} alignItems="Center" shrink="No">
          <Text as="span" size="T300">
            {`${t('common.replyTo', { name: username ?? '' })}:`}
          </Text>
        </Box>
        <Box grow="Yes">{children}</Box>
      </Box>
    );
  },
);

export const ThreadIndicator = as<'div'>(({ ...props }, ref) => (
  <Box
    shrink="No"
    className={css.ThreadIndicator}
    alignItems="Center"
    gap="100"
    {...props}
    ref={ref}
  >
    <Icon size="50" src={MessageSquareTextIcon} />
    <Text size="L400">Thread</Text>
  </Box>
));

type ReplyProps = {
  room: Room;
  timelineSet?: EventTimelineSet | undefined;
  eventId: string;
  replyEventId: string;
  relationKey?: 'm.in_reply_to' | typeof FORKS_FROM_RELATION;
  onClick?: MouseEventHandler | undefined;
};

export const Reply = as<'div', ReplyProps>(
  (
    { room, timelineSet, eventId, replyEventId, relationKey = 'm.in_reply_to', onClick, ...props },
    ref,
  ) => {
    const { t } = useTranslation();
    const placeholderWidth = useMemo(() => randomNumberBetween(40, 400), []);
    const getFromLocalTimeline = useCallback(
      () => timelineSet?.findEventById(replyEventId),
      [timelineSet, replyEventId],
    );
    const replyEvent = useRoomEvent(room, replyEventId, getFromLocalTimeline);
    const mainEvent = useRoomEvent(room, eventId);

    if (mainEvent) {
      const relatesTo = mainEvent.getWireContent()?.['m.relates_to'] as
        | ElevoEventRelation
        | undefined;
      const inReplyTo = relatesTo?.[relationKey];
      if (!inReplyTo) {
        return null;
      }

      const relation = mainEvent.getRelation();
      if (
        relationKey === 'm.in_reply_to' &&
        relation?.rel_type === THREAD_RELATION_TYPE.name &&
        relation?.is_falling_back
      ) {
        return null;
      }
    }

    const content = replyEvent?.getContent() ?? {};
    const sender = replyEvent?.getSender();
    const eventType = replyEvent?.getType();
    const { msgtype } = content;
    let body: string | undefined;
    if (msgtype === MsgType.Image) {
      body = t('message.image');
    } else if (msgtype === MsgType.File) {
      const filename = content.filename || content.body || '';
      body = t('message.file', { filename });
    } else if (eventType === 'm.sticker') {
      body = t('message.sticker');
    } else {
      body = typeof content.body === 'string' ? content.body : '';
    }

    const fallbackBody = replyEvent?.isRedacted() ? (
      <MessageDeletedContent />
    ) : (
      <MessageFailedContent />
    );

    const badEncryption = replyEvent?.getContent().msgtype === 'm.bad.encrypted';
    const bodyJSX = body ? scaleSystemEmoji(trimReplyFromBody(body)) : fallbackBody;

    return (
      <Box direction="Row" gap="200" alignItems="Center" {...props} ref={ref}>
        <Line size="500" variant="Primary" direction="Vertical" style={{ height: toRem(14) }} />
        <ReplyLayout
          as="button"
          username={
            sender ? (getMemberDisplayName(room, sender) ?? getMxIdLocalPart(sender)) : undefined
          }
          data-event-id={replyEventId}
          onClick={onClick}
        >
          {replyEvent !== undefined ? (
            <Text size="T300" truncate>
              {badEncryption ? <MessageBadEncryptedContent /> : bodyJSX}
            </Text>
          ) : (
            <LinePlaceholder
              style={{
                backgroundColor: color.SurfaceVariant.ContainerActive,
                width: toRem(placeholderWidth),
                maxWidth: '100%',
              }}
            />
          )}
        </ReplyLayout>
      </Box>
    );
  },
);
